import { createSelector } from "reselect";
import TaskList from "~/records/record.task_list.jsx";
import { sortCompare } from "~/utils/utils.jsx";
import { createImmutableIsEqualSelector } from "./index.jsx";
import { getSelectedTaskListIdFilterSelector } from "./selector.configOptions.jsx";
import { getTaskById } from "./selector.tasks.jsx";

export const getAllTaskLists = (state) => state.task_lists;

export const getCountAllTaskListsByProjectId = createSelector(
  getAllTaskLists,
  (state, projectId) => projectId,
  (taskLists, projectId) => {
    return taskLists.filter((tl) => {
      return !tl.is_trashed && tl.project_id === projectId;
    }).size;
  }
);

export const getTaskListById = (state, id) => {
  return getAllTaskLists(state).get(`${id}`) || new TaskList({ id, project_id: id });
};

export const getTaskListsByProjectId = createSelector(
  getAllTaskLists,
  (state, projectId) => projectId,
  (tasks, projectId) => {
    return tasks.filter((t) => {
      return !t.is_completed && !t.is_trashed && t.project_id === projectId;
    });
  }
);

export const getAllTaskListsByProjectId = createSelector(
  getAllTaskLists,
  (state, projectId) => projectId,
  (taskLists, projectId) => {
    let filteredTaskLists = taskLists.filter((t) => {
      return t.project_id === projectId;
    });
    return filteredTaskLists.sortBy((taskList) => taskList.position);
  }
);

export const getCompletedTaskListsByProjectId = createSelector(
  getAllTaskLists,
  (state, projectId) => projectId,
  (taskLists, projectId) => {
    return taskLists.filter((tL) => {
      return tL.is_completed && !tL.is_trashed && tL.project_id === projectId;
    });
  }
);

export const getTaskListForTaskId = createSelector(
  (state, project, taskId) => getTaskById(state, taskId),
  getAllTaskListsByProjectId,
  (task, taskLists) => {
    return taskLists.find((tl) => {
      if (tl.id === task?.task_list_id) {
        return true;
      }
    });
  }
);

const getTaskListsByProjectSelector = createSelector(getTaskListsByProjectId, (taskLists) =>
  taskLists.sortBy((taskList) => taskList.position)
);

// getProjectFiltersById is undefined, so the function is copy pasted as the second input arg
export const getFilteredTaskListsByProjectSelector = createImmutableIsEqualSelector(
  getTaskListsByProjectSelector,
  getSelectedTaskListIdFilterSelector,
  (taskLists, taskListIds) => {
    if (taskListIds?.length > 0) {
      taskLists = taskLists.filter((taskList) => taskListIds.includes(taskList.id));
    }

    return taskLists;
  }
);

export const makeFilteredTaskListIdsByProjectSelector = () => {
  return createSelector(getFilteredTaskListsByProjectSelector, (filteredTaskLists) =>
    filteredTaskLists.toList().map((tl) => tl.id)
  );
};

export const makeTaskListsByProjectSelector = () => {
  return createImmutableIsEqualSelector(getTaskListsByProjectSelector, (taskLists) => taskLists);
};

export const getProjectIdByTaskListId = createSelector(
  getTaskListById,
  (taskList) => taskList && taskList.project_id && taskList.project_id.toString()
);

export const getTaskListCompletedOnById = createSelector(getTaskListById, (taskList) => taskList.get(`completed_on`));

export const makeGetTaskListHeaderDataSelector = () => {
  return createSelector(getTaskListById, (taskList) => ({
    name: taskList.get(`name`),
    startOn: taskList.get(`start_on`),
    dueOn: taskList.get(`due_on`),
    numberOfOpenTasks: taskList.get(`open_tasks`),
    numberOfCompletedTasks: taskList.get(`completed_tasks`),
    isCompleted: taskList.get(`is_completed`, false),
  }));
};

export const getTaskListsByProjectIdForColumnView = createSelector(getTaskListsByProjectId, (taskLists) => {
  return taskLists
    .toArray()
    .sort((a, b) => sortCompare(a.get("position"), b.get("position")))
    .map((taskList) => ({
      id: taskList.get("id"),
      name: taskList.get("name"),
    }));
});

export const getAllTaskListsArrayByProjectId = createSelector(getAllTaskListsByProjectId, (taskLists) => {
  return taskLists
    .toArray()
    .sort((a, b) => sortCompare(a.get("position"), b.get("position")))
    .map((taskList) => ({
      id: taskList.get("id"),
      name: taskList.get("name"),
      isCompleted: taskList.get("is_completed"),
      isTrashed: taskList.get("is_trashed"),
    }));
});
